// Main calendar table — events grouped by day and classification.

import { Fragment } from "react";
import type { CalendarEvent, CategoryHistory } from "../types";
import { groupByDay } from "../utils/date";
import { CalendarRow } from "./CalendarRow";
import { ClassificationHeaderRow } from "./ClassificationHeaderRow";
import { DayHeader } from "./DayHeader";

interface CalendarTableProps {
  events: CalendarEvent[];
  loading?: boolean;
  showLowHigh?: boolean;
  changedEventUuids?: Set<string>;
  clearChangedStatus?: (uuid: string) => void;
  /** When "mini", data rows render a sparkline column of the category history. */
  chart?: "mini";
  /** Category history keyed by category id (for the mini-charts). */
  categoryHistory?: Record<string, CategoryHistory>;
}

export function CalendarTable({
  events,
  loading,
  showLowHigh,
  changedEventUuids,
  clearChangedStatus,
  chart,
  categoryHistory,
}: CalendarTableProps) {
  const gridClass =
    (showLowHigh ? "calendar-grid" : "calendar-grid without-low-high") +
    (chart === "mini" ? " with-chart" : "");
  const groupedDays = groupByDay(events);

  return (
    <div className={gridClass}>
      {!loading && events.length > 0 ? (
        groupedDays.map((day) => (
          <Fragment key={day.dayLabel}>
            <DayHeader day={day.dayLabel} />
            {day.classifications.map((classification) => (
              <Fragment key={`${day.dayLabel}:${classification.classification}`}>
                <div className="classification-header">
                  <span className="classification-label">
                    {classification.displayName}
                  </span>
                </div>
                <ClassificationHeaderRow
                  classification={classification.classification}
                  showLowHigh={showLowHigh}
                  chart={chart}
                />
                {classification.events.map((event) => (
                  <CalendarRow
                    key={event.uuid}
                    event={event}
                    showLowHigh={showLowHigh}
                    isChanged={changedEventUuids?.has(event.uuid || "")}
                    clearChangedStatus={clearChangedStatus}
                    isNextEvent={event.uuid === classification.nextEventUuid}
                    chart={chart}
                    categoryHistory={categoryHistory}
                  />
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))
      ) : loading ? (
        <div className="loading-indicator">Loading calendar events...</div>
      ) : null}
    </div>
  );
}

export default CalendarTable;
